import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ListaPrecioSearch from './ListaPrecioSearch';
import listaPrecioService, { ListaPrecio } from '../services/listaPrecioService';
import storeCustomerAccessService, {
  StoreCustomerAccess,
  StoreUserItem,
} from '../services/storeCustomerAccessService';

interface Props {
  nroCliente: number;
  customerEmail?: string | null;
  commercialPriceListId?: string | null;
}

const statusLabel: Record<string, string> = {
  disabled: 'No habilitado',
  enabled: 'Habilitado (sin invitación)',
  invited: 'Invitación enviada',
  active: 'Cuenta activa',
};

const statusColor: Record<string, 'default' | 'warning' | 'info' | 'success'> = {
  disabled: 'default',
  enabled: 'warning',
  invited: 'info',
  active: 'success',
};

const ClienteEcommercePanel: React.FC<Props> = ({
  nroCliente,
  customerEmail,
  commercialPriceListId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [access, setAccess] = useState<StoreCustomerAccess | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [priceListId, setPriceListId] = useState('');
  const [listaSeleccionada, setListaSeleccionada] = useState<Pick<ListaPrecio, 'codigoLista' | 'descripcion'> | null>(null);
  const [openListaSearch, setOpenListaSearch] = useState(false);
  const [users, setUsers] = useState<StoreUserItem[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitingUser, setInvitingUser] = useState(false);
  const [resendingUserId, setResendingUserId] = useState<number | null>(null);
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await storeCustomerAccessService.getAccess(nroCliente);
      setAccess(data);
      setEnabled(data.enabled);
      setPriceListId(data.priceListId || '');
      try {
        const storeUsers = await storeCustomerAccessService.listUsers(nroCliente);
        setUsers(storeUsers);
      } catch {
        setUsers([]);
      }
      if (data.priceListId) {
        try {
          const lista = await listaPrecioService.getListaPrecio(data.priceListId);
          setListaSeleccionada({ codigoLista: lista.codigoLista, descripcion: lista.descripcion });
        } catch {
          setListaSeleccionada({ codigoLista: data.priceListId, descripcion: '' });
        }
      } else {
        setListaSeleccionada(null);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo cargar el acceso ecommerce', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [nroCliente, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await storeCustomerAccessService.upsertAccess(nroCliente, {
        enabled,
        priceListId: priceListId || null,
      });
      setAccess(data);
      enqueueSnackbar('Acceso ecommerce actualizado', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    setInviting(true);
    try {
      if (!enabled) {
        await storeCustomerAccessService.upsertAccess(nroCliente, {
          enabled: true,
          priceListId: priceListId || null,
        });
        setEnabled(true);
      }
      await storeCustomerAccessService.invite(nroCliente);
      enqueueSnackbar('Invitación enviada por email', { variant: 'success' });
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo enviar la invitación', {
        variant: 'error',
      });
    } finally {
      setInviting(false);
    }
  };

  const handleInviteUser = async () => {
    const email = inviteEmail.trim();
    if (!email) {
      enqueueSnackbar('Ingresá el email del usuario', { variant: 'warning' });
      return;
    }
    setInvitingUser(true);
    try {
      if (!enabled) {
        await storeCustomerAccessService.upsertAccess(nroCliente, {
          enabled: true,
          priceListId: priceListId || null,
        });
        setEnabled(true);
      }
      await storeCustomerAccessService.inviteUser(nroCliente, {
        email,
        displayName: inviteName.trim() || null,
      });
      enqueueSnackbar('Invitación enviada al usuario', { variant: 'success' });
      setInviteEmail('');
      setInviteName('');
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo invitar al usuario', {
        variant: 'error',
      });
    } finally {
      setInvitingUser(false);
    }
  };

  const handleResendUser = async (user: StoreUserItem) => {
    setResendingUserId(user.id);
    try {
      await storeCustomerAccessService.resendUserInvite(nroCliente, user.id);
      enqueueSnackbar(`Mail de activación reenviado a ${user.email}`, { variant: 'success' });
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo reenviar la activación', {
        variant: 'error',
      });
    } finally {
      setResendingUserId(null);
    }
  };

  const handleToggleUser = async (user: StoreUserItem) => {
    setTogglingUserId(user.id);
    try {
      if (user.enabled) {
        await storeCustomerAccessService.disableUser(nroCliente, user.id);
        enqueueSnackbar('Usuario deshabilitado', { variant: 'success' });
      } else {
        await storeCustomerAccessService.enableUser(nroCliente, user.id);
        enqueueSnackbar('Usuario habilitado', { variant: 'success' });
      }
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo actualizar el usuario', {
        variant: 'error',
      });
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      const data = await storeCustomerAccessService.disable(nroCliente);
      setAccess(data);
      setEnabled(false);
      enqueueSnackbar('Acceso deshabilitado y sesiones revocadas', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al deshabilitar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Typography variant="h6">eCommerce</Typography>
        <Chip
          size="small"
          label={statusLabel[access?.status || 'disabled'] || access?.status}
          color={statusColor[access?.status || 'disabled'] || 'default'}
        />
      </Stack>

      {!customerEmail && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          El cliente necesita un email en la ficha para habilitar e invitar a la tienda.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={!customerEmail && !enabled}
              />
            }
            label="Habilitar en tienda online"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Lista de precios ecommerce (opcional)"
            value={
              listaSeleccionada
                ? `${listaSeleccionada.codigoLista.trim()} - ${listaSeleccionada.descripcion?.trim() || ''}`
                : priceListId || ''
            }
            fullWidth
            margin="normal"
            helperText={
              commercialPriceListId
                ? `Si queda vacío, se usa la lista por defecto de la tienda. Si esa también está vacía, la comercial (${commercialPriceListId}).`
                : 'Si queda vacío, se usa la lista por defecto de la tienda y, si no hay, la comercial del cliente.'
            }
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setOpenListaSearch(true)} edge="end" color="primary">
                    <ArrowDropDownIcon />
                  </IconButton>
                  {priceListId && (
                    <Button
                      size="small"
                      onClick={() => {
                        setPriceListId('');
                        setListaSeleccionada(null);
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" mt={2}>
            Email invitación: <strong>{access?.invitedEmail || customerEmail || '—'}</strong>
          </Typography>
          {access?.invitedAt && (
            <Typography variant="body2" color="text.secondary">
              Invitado: {new Date(access.invitedAt).toLocaleString()}
            </Typography>
          )}
          {access?.activatedAt && (
            <Typography variant="body2" color="text.secondary">
              Activado: {new Date(access.activatedAt).toLocaleString()}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar acceso'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleInvite}
              disabled={inviting || !customerEmail}
            >
              {inviting ? 'Enviando…' : access?.invitedAt ? 'Reenviar invitación' : 'Enviar invitación'}
            </Button>
            {access?.enabled && (
              <Button variant="outlined" color="error" onClick={handleDisable} disabled={saving}>
                Deshabilitar
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Usuarios de la tienda
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cada persona entra con su email y tiene su propio carrito. Los precios y pedidos siguen siendo del cliente.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <TextField
            label="Email del usuario"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Nombre (opcional)"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={3} display="flex" alignItems="center">
          <Button variant="outlined" onClick={handleInviteUser} disabled={invitingUser || !inviteEmail.trim()}>
            {invitingUser ? 'Enviando…' : 'Invitar usuario'}
          </Button>
        </Grid>
      </Grid>

      {users.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Todavía no hay usuarios. Al enviar la invitación principal se crea el primero.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {users.map((user) => (
            <Stack
              key={user.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {user.displayName || user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                  {user.activatedAt
                    ? ` · activo ${new Date(user.activatedAt).toLocaleString()}`
                    : ' · pendiente de activación'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {!user.activatedAt && user.enabled && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleResendUser(user)}
                    disabled={resendingUserId === user.id}
                  >
                    {resendingUserId === user.id ? 'Enviando…' : 'Reenviar activación'}
                  </Button>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={user.enabled}
                      disabled={togglingUserId === user.id}
                      onChange={() => handleToggleUser(user)}
                    />
                  }
                  label={user.enabled ? 'Habilitado' : 'Deshabilitado'}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <ListaPrecioSearch
        open={openListaSearch}
        onClose={() => setOpenListaSearch(false)}
        onSelect={(lista) => {
          setListaSeleccionada({ codigoLista: lista.codigoLista, descripcion: lista.descripcion });
          setPriceListId(lista.codigoLista.trim());
          setOpenListaSearch(false);
        }}
      />
    </Box>
  );
};

export default ClienteEcommercePanel;
